import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';

const CHART_GROUP_ID = 'listing-volume-linked-group';
const PAGE_SIZE = 10;

const toDateKey = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = text.replace(/[./]/g, '-');
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  if (/^\d{4}-\d{2}$/.test(normalized)) return `${normalized}-01`;
  if (/^\d{6}$/.test(text)) {
    const yy = Number(text.slice(0, 2));
    const yyyy = yy >= 70 ? 1900 + yy : 2000 + yy;
    return `${yyyy}-${text.slice(2, 4)}-${text.slice(4, 6)}`;
  }
  if (/^\d{8}$/.test(text)) return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) return normalized.slice(0, 10);
  return normalized.length >= 10 ? normalized.slice(0, 10) : normalized;
};

const toMonthKey = (value) => {
  const dateKey = toDateKey(value);
  return dateKey ? dateKey.slice(0, 7) : '';
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return null;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toNullableNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// 날짜 표기 통일: YY.MM.DD (일 없으면 YY.MM)
const formatDotDate = (value) => {
  const d = toDateKey(value);
  const m = String(d || '').match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return d ? d.replace(/-/g, '.') : '-';
  const yy = m[1].slice(2);
  return m[3] ? `${yy}.${m[2]}.${m[3]}` : `${yy}.${m[2]}`;
};

const renderDelta = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n > 0) return <span className="delta up">▲{Math.abs(n).toLocaleString('ko-KR')}</span>;
  if (n < 0) return <span className="delta down">▼{Math.abs(n).toLocaleString('ko-KR')}</span>;
  return '-';
};

const buildOption = (labels, values, name, color) => {
  return {
    grid: { left: 40, right: 14, top: 24, bottom: 34 },
    tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
    dataZoom: [{
      type: 'inside',
      start: 0,
      end: 100,
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      moveOnMouseWheel: false,
    }],
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { formatter: (v) => formatDotDate(v) },
    },
    yAxis: { type: 'value' },
    series: [{
      name,
      type: 'bar',
      data: values,
      barWidth: 12,
      itemStyle: { color, borderRadius: [4, 4, 0, 0] },
    }],
  };
};

function ListingVolumeChartSection({
  rows = [],
  loading = false,
  onRequestRange,
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const zoomDebounceRef = useRef(null);
  const lastRangeRef = useRef('');

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setLoadingMore(false);
    lastRangeRef.current = '';
  }, [rows]);

  useEffect(() => () => {
    if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    echarts.disconnect(CHART_GROUP_ID);
  }, []);

  const { labels, saleValues, leaseValues, tableRows } = useMemo(() => {
    const raw = Array.isArray(rows) ? rows : [];

    const weeklySorted = raw
      .map((r) => ({
        baseDate: toDateKey(pick(r, 'baseDate', 'BaseDate', 'base_date')),
        saleCount: toNullableNum(pick(r, 'saleCount', 'SaleCount', 'sale_count')),
        leaseCount: toNullableNum(pick(r, 'leaseCount', 'LeaseCount', 'lease_count')),
      }))
      .filter((r) => r.baseDate)
      .sort((a, b) => String(a.baseDate).localeCompare(String(b.baseDate)));

    const labels = weeklySorted.map((r) => r.baseDate);
    const sale = weeklySorted.map((r) => toNum(r.saleCount));
    const lease = weeklySorted.map((r) => toNum(r.leaseCount));

    const weeklyDesc = [...weeklySorted].sort((a, b) => String(b.baseDate).localeCompare(String(a.baseDate)));

    const rowsWithDelta = weeklyDesc.map((row, idx) => {
      const older = weeklyDesc[idx + 1];
      const saleDelta = older && row.saleCount !== null && older.saleCount !== null
        ? row.saleCount - older.saleCount
        : null;
      const leaseDelta = older && row.leaseCount !== null && older.leaseCount !== null
        ? row.leaseCount - older.leaseCount
        : null;
      return {
        ...row,
        saleDelta,
        leaseDelta,
      };
    });

    return {
      labels,
      saleValues: sale,
      leaseValues: lease,
      tableRows: rowsWithDelta,
    };
  }, [rows]);

  const saleOption = useMemo(
    () => buildOption(labels, saleValues, '매매 매물량', '#0ea5e9'),
    [labels, saleValues]
  );
  const leaseOption = useMemo(
    () => buildOption(labels, leaseValues, '전세 매물량', '#10b981'),
    [labels, leaseValues]
  );

  const linkChart = (chart) => {
    chart.group = CHART_GROUP_ID;
    echarts.connect(CHART_GROUP_ID);
  };

  const handleChartDataZoom = (event) => {
    if (!onRequestRange || !labels.length) return;
    const batchItem = Array.isArray(event?.batch) ? event.batch[0] : event;
    const startPercent = Number(batchItem?.start);
    const endPercent = Number(batchItem?.end);
    if (!Number.isFinite(startPercent) || !Number.isFinite(endPercent)) return;

    const safeStart = Math.max(0, Math.min(100, startPercent));
    const safeEnd = Math.max(0, Math.min(100, endPercent));
    const startIdx = Math.max(0, Math.floor(((labels.length - 1) * safeStart) / 100));
    const endIdx = Math.min(labels.length - 1, Math.ceil(((labels.length - 1) * safeEnd) / 100));
    const fromMonth = labels[startIdx];
    const toMonth = labels[endIdx];
    if (!fromMonth || !toMonth) return;

    if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    zoomDebounceRef.current = setTimeout(() => {
      const rangeKey = `${fromMonth}|${toMonth}`;
      if (lastRangeRef.current === rangeKey) return;
      lastRangeRef.current = rangeKey;
      const spanPercent = safeEnd - safeStart;
      if (spanPercent >= 99) {
        onRequestRange({ periodYears: 3, fromDate: null, toDate: null });
        return;
      }
      onRequestRange({ periodYears: null, fromDate: `${fromMonth}-01`, toDate: `${toMonth}-31` });
    }, 250);
  };

  const visibleRows = tableRows.slice(0, visibleCount);
  const hasMore = visibleCount < tableRows.length;

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount((prev) => prev + PAGE_SIZE);
      setLoadingMore(false);
    }, 250);
  };

  const hasData = labels.length > 0 && (saleValues.some(v => v > 0) || leaseValues.some(v => v > 0));

  return (
    <section className="tx-sub-chart-section">
      <div className="tx-sub-chart-head">
        <div>
          <h4>매물량</h4>
        </div>
      </div>

      {hasData ? (
        <div className="tx-volume-content-row">
          <div className="tx-volume-chart-pane">
            <div>
              <h5 style={{ margin: '0 0 6px', fontSize: '14px', color: '#334155' }}>매매</h5>
              <ReactECharts
                option={saleOption}
                style={{ width: '100%', height: 190 }}
                notMerge
                lazyUpdate
                onChartReady={linkChart}
                onEvents={{ datazoom: handleChartDataZoom }}
              />
            </div>

            <div style={{ marginTop: 8 }}>
              <h5 style={{ margin: '0 0 6px', fontSize: '14px', color: '#334155' }}>전세</h5>
              <ReactECharts
                option={leaseOption}
                style={{ width: '100%', height: 190 }}
                notMerge
                lazyUpdate
                onChartReady={linkChart}
                onEvents={{ datazoom: handleChartDataZoom }}
              />
            </div>

            {loading ? <div className="tx-volume-loading">매물량 데이터를 불러오는 중입니다...</div> : null}
          </div>

          <div className="tx-volume-table-pane">
            <div className="tx-volume-table-wrap">
              <table className="tx-volume-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>날짜</th>
                    <th colSpan={2}>매매 매물</th>
                    <th colSpan={2}>전세 매물</th>
                  </tr>
                  <tr>
                    <th>매물수</th>
                    <th>주간변동수</th>
                    <th>매물수</th>
                    <th>주간변동수</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, idx) => (
                    <tr key={`${row.baseDate}-${idx}`}>
                      <td>{formatDotDate(row.baseDate)}</td>
                      <td>{row.saleCount === null ? '-' : row.saleCount.toLocaleString('ko-KR')}</td>
                      <td>{renderDelta(row.saleDelta)}</td>
                      <td>{row.leaseCount === null ? '-' : row.leaseCount.toLocaleString('ko-KR')}</td>
                      <td>{renderDelta(row.leaseDelta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore ? (
              <button type="button" className="tx-table-more-btn" onClick={handleLoadMore} disabled={loadingMore || loading}>
                {loadingMore ? '불러오는 중..' : '더보기'}
              </button>
            ) : null}
          </div>
        </div>
      ) : loading ? (
        <div className="ranking-detail-modal-status">매물량 데이터를 불러오는 중입니다...</div>
      ) : (
        <div className="ranking-detail-modal-status">매물량 데이터가 없습니다.</div>
      )}
    </section>
  );
}

export default ListingVolumeChartSection;
